// © 2025 Camp Promethean, LLC. All Rights Reserved.

/**
 * Product UI screenshots for marketing pages.
 * Drop WebP files into `public/marketing/product/` — see README there.
 */

export interface ProductImage {
  key: string;
  src: string;
  placeholder?: string;
  alt: string;
  label?: string;
}

const BASE = "/marketing/product";

const product = (
  key: string,
  filename: string,
  alt: string,
  label?: string,
): ProductImage => ({
  key,
  src: `${BASE}/${filename}`,
  placeholder: filename.includes(".webp")
    ? `${BASE}/${filename.replace(".webp", "-blur.webp")}`
    : undefined,
  alt,
  label,
});

export const productImages = {
  heroDashboard: product(
    "heroDashboard",
    "hero-dashboard.webp",
    "Camp Promethean dashboard overview",
    "Dashboard",
  ),
  moduleArca: product(
    "moduleArca",
    "modules-arca.webp",
    "Arca cultivation toolkit module",
    "Arca",
  ),
  moduleAnimus: product(
    "moduleAnimus",
    "modules-animus.webp",
    "Animus genetics and breeding module",
    "Animus",
  ),
  moduleAurum: product(
    "moduleAurum",
    "modules-aurum.webp",
    "Aurum compliance and SOPs module",
    "Aurum",
  ),
  moduleAtlas: product(
    "moduleAtlas",
    "modules-atlas.webp",
    "Atlas production planning module",
    "Atlas",
  ),
  moduleAncora: product(
    "moduleAncora",
    "modules-ancora.webp",
    "Ancora academy and training module",
    "Ancora",
  ),
  moduleAgora: product(
    "moduleAgora",
    "modules-agora.webp",
    "Agora consultant marketplace module",
    "Agora",
  ),
  modulePrometheus: product(
    "modulePrometheus",
    "modules-prometheus.webp",
    "Prometheus AI assistant",
    "Prometheus",
  ),
  workflowCapture: product(
    "workflowCapture",
    "workflow-capture.webp",
    "Capture data in the grow room",
    "Capture",
  ),
  workflowConnect: product(
    "workflowConnect",
    "workflow-connect.webp",
    "Connected cultivation data",
    "Connect",
  ),
  workflowCompound: product(
    "workflowCompound",
    "workflow-compound.webp",
    "AI insights from your runs",
    "Compound",
  ),
  solutionsOverview: product(
    "solutionsOverview",
    "solutions-overview.webp",
    "Camp Promethean platform overview",
    "Platform",
  ),
  pricingProfessional: product(
    "pricingProfessional",
    "solutions-tier-pro.png",
    "Prometheus — cultivation co-pilot on Camp Promethean",
    "Prometheus",
  ),
  pricingBasic: product(
    "pricingBasic",
    "solutions-tier-basic.png",
    "Arca cultivation dashboard — runoff, applications, and daily horticulture ops",
    "Arca · Ancora",
  ),
  pricingAdvanced: product(
    "pricingAdvanced",
    "solutions-tier-advanced.png",
    "Animus genetics workspace — strain library, archive, and pheno hunts",
    "Animus · Agora",
  ),
} as const satisfies Record<string, ProductImage>;

export type ProductImageKey = keyof typeof productImages;

/** Keys with files present under `public/marketing/product/`. Update when assets are added. */
export const AVAILABLE_PRODUCT_IMAGE_KEYS = new Set<ProductImageKey>([
  "moduleAnimus",
  "pricingBasic",
  "pricingAdvanced",
  "pricingProfessional",
]);

export function isProductImageAvailable(key: ProductImageKey | string): boolean {
  return AVAILABLE_PRODUCT_IMAGE_KEYS.has(key as ProductImageKey);
}

export function getProductImage(key: ProductImageKey): ProductImage {
  return productImages[key];
}
